import React from "react";
import { Box, Tooltip, useMediaQuery } from "@mui/material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferMachine } from "../../models/models";

interface OfferMachineCardProps {
  machine?: OfferMachine;
}

const OfferMachineCard: React.FC<OfferMachineCardProps> = ({ machine }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  if (!machine) return null;

  const imageUrl = machine.imageUrl || machine.catalogImageUrl;
  const machineName = machine.brandName ? `${machine.brandName} ${machine.name}` : machine.name;

  // Phone: horizontal compact
  if (isPhone) {
    return (
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: dt.radius,
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={machine.name}
            sx={{
              width: 48,
              height: 48,
              borderRadius: dt.radiusSm,
              objectFit: "cover",
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: dt.radiusSm,
              backgroundColor: dt.inputBg,
              border: `1px solid ${dt.cardBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 22, color: dt.textMuted }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={machineName} enterDelay={400} arrow>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: "#1E293B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {machineName}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Desktop / Tablet: vertical card
  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        overflow: "hidden",
      }}
    >
      {/* Section label */}
      <Box
        sx={{
          padding: "14px 16px 0 16px",
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "8px",
        }}
      >
        {translate("offerDetail.machineInfo")}
      </Box>

      {/* Photo */}
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={machine.name}
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: 200,
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 120,
            backgroundColor: dt.inputBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 36, color: dt.textMuted, opacity: 0.5 }} />
        </Box>
      )}

      {/* Info */}
      <Box sx={{ padding: "14px 16px" }}>
        <Tooltip title={machineName} enterDelay={400} arrow>
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: "#1E293B",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {machineName}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default OfferMachineCard;
